/**
 *  A collection of global functions and variables
 *  that can be used throughout the application.
 *
 *  @version 0.2.0
 */


/**
 * Global Variables
 */
const mobileMaxWidth = 767;
const html = document.documentElement;
const body = document.body;

const recaptchaSiteKeyMeta = document.querySelector('meta[name="recaptcha-site-key"]');
const recaptchaSiteKey = recaptchaSiteKeyMeta?.getAttribute('content');

/**
 * Check if the current device is a mobile device
 * @type {boolean}
 */
window.isMobile = window.innerWidth <= mobileMaxWidth;
window.addEventListener('resize', () => {
    window.isMobile = window.innerWidth <= mobileMaxWidth;
});

// Shared breakpoint used across carousels
export const TABLET_LANDSCAPE = 1067;

/**
 * DOM helpers — single source of truth
 */
export const select = (sel, ctx = document) => ctx.querySelector(sel);
export const selectAll = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

/**
 * Prevent Body Scroll
 */
const preventBodyScroll = () => body.classList.add('noscroll');

/**
 * Allow Body Scroll
 */
const allowBodyScroll = () => body.classList.remove('noscroll');


/**
 * CSRF Token
 */
window.csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

/**
 * Scroll to Element
 * @param {string|Element} selector A CSS selector string or DOM element to scroll to
 * @param {{offset?: number, behavior?: ScrollBehavior, focus?: boolean}} [options] Optional scrolling options
 */
window.scrollToElement = (selector, options = {}) => {
    const el = typeof selector === 'string' ? select(selector) : selector;
    if (!el) return;
    const yOffset = typeof options.offset === 'number' ? options.offset : -100;
    const prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const top = el.getBoundingClientRect().top + window.scrollY + yOffset;
    window.scrollTo({top, behavior: prefersReduced ? 'auto' : (options.behavior || 'smooth')});
    if (options.focus === true && typeof el.focus === 'function') {
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
        el.focus({preventScroll: true});
    }
};


/**
 * Show Modal by providing its ID
 * @param id
 */
window.showModal = id => {
    const modal = select(`#modals_container .modal[data-modal-id="${id}"]`);
    if (!modal) return;
    modal.classList.add('visible');
    modal.setAttribute('aria-hidden', 'false');
    modal.setAttribute('aria-modal', 'true');
    if (!modal.hasAttribute('role')) modal.setAttribute('role', 'dialog');
    modal.dispatchEvent(new Event('show'));
    const focusTarget =
        $('[data-focus-on-show-modal]', modal)
        || modal.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
        || modal;
    if (typeof focusTarget.focus === 'function') focusTarget.focus();
};


/**
 * Toggle Pop Out Menu
 * @param id
 * @param forceState
 */
window.togglePopOutMenu = (id, forceState = null) => {
    const menu = select(`[data-pop-out-menu-id="${id}"]`);
    const buttons = selectAll(`[data-pop-out-menu-toggle="${id}"]`);

    if (!menu) return;

    const apply = (element, action) => {
        if (!element) return;
        element.classList[action]('active');
    };

    const setAriaExpanded = expanded => {
        buttons.forEach(button => button.setAttribute('aria-expanded', String(expanded)));
    };

    if (forceState === 'close') {
        apply(menu, 'remove');
        buttons.forEach(btn => apply(btn, 'remove'));
        setAriaExpanded(false);
        allowBodyScroll();
        document.querySelector('.hamburger_container')?.querySelector('.hamburger')?.classList.remove('active');
    } else if (forceState === 'open') {
        apply(menu, 'add');
        buttons.forEach(btn => apply(btn, 'add'));
        setAriaExpanded(true);
    } else {
        menu.classList.toggle('active');
        buttons.forEach(btn => btn.classList.toggle('active'));
        setAriaExpanded(menu.classList.contains('active'));
    }

    menu.classList.contains('active') ? preventBodyScroll() : allowBodyScroll();
};

/**
 * Show Modal
 * @param content
 * @param title
 * @param rich
 */
window.modal = (content, title = 'Message', rich = false) => {
    const dyn = $('#modals_container .modal[data-modal-id="dynamic"]');
    if (!dyn) return;
    const header = dyn.querySelector('.modal_header');
    let heading = header?.querySelector('h4');

    if (title) {
        if (!heading && header) {
            heading = document.createElement('h4');
            const span = document.createElement('span');
            heading.appendChild(span);

            const closeButton = header.querySelector('.modal_close_button');
            if (closeButton) {
                header.insertBefore(heading, closeButton);
            } else {
                header.prepend(heading);
            }
        }

        heading?.querySelector('span')?.replaceChildren(document.createTextNode(title));
    } else {
        heading?.remove();
    }

    dyn.querySelector('.modal_content').innerHTML = rich ? content : `<p>${content}</p>`;
    window.showModal('dynamic');
};

/**
 * Create Map
 * @param target
 * @param lat
 * @param lng
 * @param zoom
 * @param options
 * @returns {Promise<any>}
 */
window.createMap = (target, lat, lng, zoom = 8, options = {}) => {
    if (typeof target === 'string') {
        target = document.querySelector(target);
    }
    if (!target) return;

    const latitude = Number(lat);
    const longitude = Number(lng);
    const showMarker = options.showMarker ?? true;
    const mapId = options.mapId ?? Math.random().toString(6).substring(2, 11);

    // Return the promise, but also self-handle errors internally
    const promise = (async () => {
        const {Map} = await google.maps.importLibrary('maps');
        let markerLib = null;

        if (showMarker) {
            markerLib = await google.maps.importLibrary('marker');
        }

        const map = new Map(target, {
            center: {lat: latitude, lng: longitude},
            zoom,
            ...(mapId ? {mapId} : {}),
        });

        if (showMarker && markerLib) {
            const {AdvancedMarkerElement} = markerLib;
            new AdvancedMarkerElement({
                position: {lat: latitude, lng: longitude},
                map,
            });
        }

        return map;
    })();

    // Optional: swallow unhandled rejections when caller ignores the promise
    promise.catch(err => console.error('Map failed to load:', err));

    return promise;

};

window.initEmbeddedMaps = (attempt = 0) => {
    if (attempt > 10) {
        console.error('Google Maps failed to load after multiple attempts.');
        return;
    }

    if (!window.google || !google.maps || !google.maps.importLibrary) {
        // retry after 200ms
        return setTimeout(() => initEmbeddedMaps(attempt + 1), 200);
    }

    const embeddedMaps = selectAll('.embedded-map');
    if (!embeddedMaps.length) return;

    embeddedMaps.forEach(mapEl => {
        createMap(
            mapEl,
            mapEl.dataset.lat,
            mapEl.dataset.lng,
            15,
            {showMarker: true}
        );
    });
}

window.fadeLazyLoadImage = function (image) {
    if (image.complete) {
        // If the image is already loaded from cache, add loaded class immediately
        image.classList.add("loaded");
    } else {
        image.addEventListener("load", () => {
            image.decode().then(() => {
                image.classList.add("loaded");
            }).catch(() => {
                // If something went wrong, add loaded class anyway
                image.classList.add("loaded");
            });
        });
    }
}

window.initPictureFade = function (picture) {
    const img = picture.querySelector('img');
    if (!img) return;
    if (!picture.querySelectorAll('source[media]').length) return;

    let knownSrc = img.currentSrc;

    img.addEventListener('load', () => {
        if (img.currentSrc === knownSrc) return;
        knownSrc = img.currentSrc;

        img.decode().then(() => {
            // Snap opacity to 0 without animating, then fade the new source in
            img.style.transition = 'none';
            img.classList.remove('loaded');
            img.offsetHeight; // force reflow
            img.style.transition = '';
            img.classList.add('loaded');
        }).catch(() => {
            img.classList.add('loaded');
        });
    });
};

window.observeLazyImages = function () {
    const observer = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
            for (const node of mutation.addedNodes) {
                if (node.nodeType !== Node.ELEMENT_NODE) continue;
                if (node.matches('img[loading="lazy"]')) {
                    fadeLazyLoadImage(node);
                }
                node.querySelectorAll('img[loading="lazy"]').forEach(fadeLazyLoadImage);
                if (node.matches('picture')) {
                    initPictureFade(node);
                }
                node.querySelectorAll('picture').forEach(initPictureFade);
            }
        }
    });
    observer.observe(document.body, { childList: true, subtree: true });
};

window.displayTab = function (target) {
    let button = select('[action-display-tab="' + target + '"]');
    let tab = select('[data-tab="' + target + '"]');
    if (!button || !tab) return;

    button.classList.add('active');
    tab.classList.add('open');
}

window.hideTab = function (target) {
    let button = select('[action-display-tab="' + target + '"]');
    let tab = select('[data-tab="' + target + '"]');
    if (!button || !tab) return;

    button.classList.remove('active');
    tab.classList.remove('open');
}

// --------------------------
// Carousel shared utilities
// --------------------------

/**
 * Get the width of the right edge of a container, taking into account the viewport width.
 * @param el
 * @returns {number}
 */
export const getRightGutter = (el) => {
    const container = el.closest('.container');
    if (!container) return 0;
    const viewportWidth = Math.round(
        (window.visualViewport && window.visualViewport.width) ||
        document.documentElement.clientWidth ||
        window.innerWidth || 0
    );
    const right = Math.ceil(container.getBoundingClientRect().right);
    return Math.max(0, viewportWidth - right);
};

/**
 * Debounce a function.
 * @param fn
 * @param wait
 * @returns {(function(...[*]): void)|*}
 */
export const debounce = (fn, wait = 120) => {
    let t;
    return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(null, args), wait);
    };
};

/**
 * Equalize heights of slides in a Swiper container.
 * @param containerEl
 */
export const equalizeSlidesHeight = (containerEl) => {
    if (!containerEl) return;
    const slides = containerEl.querySelectorAll('.swiper-slide');
    if (!slides.length) return;

    // Reset previous sizing before measurement
    slides.forEach(slide => {
        const target = slide.querySelector('[data-eq-height]') || slide.firstElementChild || slide;
        if (target) target.style.minHeight = '';
    });

    let maxH = 0;
    slides.forEach(slide => {
        const target = slide.querySelector('[data-eq-height]') || slide.firstElementChild || slide;
        if (!target) return;
        const h = target.getBoundingClientRect().height;
        if (h > maxH) maxH = h;
    });

    if (maxH > 0) {
        const hpx = Math.ceil(maxH) + 'px';
        slides.forEach(slide => {
            const target = slide.querySelector('[data-eq-height]') || slide.firstElementChild || slide;
            if (target) target.style.minHeight = hpx;
        });
    }
};

/**
 * Tabbed Form Management
 */
export const initTabbedForms = function () {
    const formTabbedGroups = document.querySelectorAll('.form-tabbed-groups');

    const updateFormHeight = (container) => {
        if (!container) return;
        const activeTab = container.querySelector('.tabbed-form-group.active');
        if (activeTab) {
            const threshold = window.innerHeight * 0.1;
            const contentHeight = activeTab.offsetHeight;
            container.style.height = Math.max(contentHeight, threshold) + 'px';

            // If the active tab contains images that are still loading, recalc height when they finish
            if (typeof attachImageLoadEqualizer === 'function') {
                attachImageLoadEqualizer(activeTab, () => updateFormHeight(container));
            } else {
                // Fallback in case helper is unavailable
                const imgs = activeTab.querySelectorAll('img');
                imgs.forEach(img => {
                    if (!img.complete) {
                        img.addEventListener('load', () => updateFormHeight(container), {once: true});
                        img.addEventListener('error', () => updateFormHeight(container), {once: true});
                    }
                });
            }
        }
    };

    window.recalculateFormHeight = () => {
        formTabbedGroups.forEach(container => updateFormHeight(container));
    };

    function scrollToFormTop(container) {
        if (!container) return;
        if (window.scrollToElement) {
            window.scrollToElement(container, {offset: -100});
        } else {
            container.scrollIntoView({behavior: 'smooth', block: 'start'});
        }
    }

    formTabbedGroups.forEach(container => {
        // Initial height
        if (document.readyState === 'complete') {
            updateFormHeight(container);
        } else {
            window.addEventListener('load', () => updateFormHeight(container));
        }
        window.addEventListener('resize', () => updateFormHeight(container));

        // Handle Tab Transitions
        container.addEventListener('click', (e) => {
            const continueBtn = e.target.closest('[data-action="form-continue"]');
            const backBtn = e.target.closest('[data-action="form-back"]');

            if (!continueBtn && !backBtn) return;

            const button = continueBtn || backBtn;
            const currentEl = button.closest('.tabbed-form-group');
            if (!currentEl) return;

            if (continueBtn) {
                // Simple validation for required fields in the current group
                let hasError = false;
                const requiredFields = currentEl.querySelectorAll('[required]');

                requiredFields.forEach(field => {
                    let isInvalid = false;
                    if (field.type === 'radio' || field.type === 'checkbox') {
                        const name = field.getAttribute('name');
                        const checked = currentEl.querySelector(`input[name="${name}"]:checked`);
                        isInvalid = !checked;
                    } else {
                        isInvalid = !field.value.trim();
                    }

                    const containerGroup = field.closest('.input-group, .input-group--checkboxes');
                    if (isInvalid) {
                        hasError = true;
                        field.classList.add('error');
                        if (containerGroup) containerGroup.classList.add('error');
                    } else {
                        field.classList.remove('error');
                        if (containerGroup) containerGroup.classList.remove('error');
                    }
                });

                if (hasError) {
                    updateFormHeight(container);
                    const firstError = currentEl.querySelector('.error');
                    if (firstError) {
                        if (window.scrollToElement) {
                            window.scrollToElement(firstError, {offset: -150});
                        } else {
                            firstError.scrollIntoView({behavior: 'smooth', block: 'center'});
                        }
                    }
                    return;
                }
            } else if (backBtn) {
                // Clear any error states on the current tab before going back
                currentEl.querySelectorAll('.error').forEach(el => el.classList.remove('error'));
            }

            const targetGroup = button.getAttribute('data-target');
            const targetEl = container.querySelector(`.tabbed-form-group[data-group="${targetGroup}"]`);

            if (targetEl) {
                document.dispatchEvent(new CustomEvent('form-tab-changing', {detail: {targetGroup}}));
                currentEl.classList.remove('active');
                targetEl.classList.add('active');

                requestAnimationFrame(() => {
                    updateFormHeight(container);
                    scrollToFormTop(container);
                });

                document.dispatchEvent(new CustomEvent('form-tab-changed', {detail: {targetGroup}}));
            }
        });

        // Remove error class on input/change
        const clearError = (e) => {
            const field = e.target;
            if (!field.hasAttribute('required')) return;

            field.classList.remove('error');
            const containerGroup = field.closest('.input-group, .input-group--checkboxes');
            if (containerGroup) {
                containerGroup.classList.remove('error');
                updateFormHeight(container);
            }
        };

        container.addEventListener('input', clearError);
        container.addEventListener('change', clearError);
    });
};

/**
 * Reset Swiper inline heights.
 * @param containerEl
 */
export const resetSwiperInlineHeights = (containerEl) => {
    if (!containerEl) return;
    containerEl.style.height = '';
    const wrapper = containerEl.querySelector('.swiper-wrapper');
    if (wrapper) wrapper.style.height = '';
    containerEl.querySelectorAll('.swiper-slide').forEach(slide => {
        slide.style.height = '';
    });
};

/**
 * Attach image load equalizer to a container element.
 * @param containerEl
 * @param run
 */
export const attachImageLoadEqualizer = (containerEl, run) => {
    if (!containerEl) return;
    const imgs = containerEl.querySelectorAll('img');
    imgs.forEach(img => {
        if (img.complete) return; // already loaded
        img.addEventListener('load', () => run(), {once: true});
        img.addEventListener('error', () => run(), {once: true});
    });
};


/**
 * Load Google reCAPTCHA script asynchronously.
 * @param callback
 */
export const loadReCaptcha = function (callback) {
    if (typeof grecaptcha !== 'undefined') {
        callback();
        return;
    }

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = callback;
    document.head.appendChild(script);
}

export const padSlides = function(swiperEl, minSlides = 12) {
    const wrapper = swiperEl.querySelector(".swiper-wrapper");
    if (!wrapper) return;

    let slides = Array.from(wrapper.children).filter((n) =>
        n.classList.contains("swiper-slide")
    );

    if (slides.length >= minSlides) return;

    while (slides.length < minSlides) {
        slides.forEach((slide) => {
            const node = slide.cloneNode(true);
            node.querySelector('img')?.classList?.add('loaded');
            wrapper.appendChild(node)
        });
        slides = Array.from(wrapper.children).filter((n) =>
            n.classList.contains("swiper-slide")
        );
    }
}
